import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { NotificationService } from './notification.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  themeType: any;
  public cartDataSource = new BehaviorSubject<any>(null);
  public cartResponse$ = new Subject<any>();
  public isCmsView = new BehaviorSubject<any>(null);
  public cartLoading = new BehaviorSubject<any>(false);
  private sidenavToggleSource = new Subject<boolean>();
  public triggerCreditCard$ = new Subject<boolean>();
  public creditCardDelined$ = new Subject<any>();
  private drawerState = new Subject<boolean>();
  public productDialog$ = new Subject<boolean>();
  public creditcadMsg: any;
  public recurringUpdate$ = new Subject<any>();
  public vacationUpdate$ = new Subject<any>();
  private previousUrl: string | null = null;
  redirectToMembership: boolean = false;
  private passEventSubject = new Subject<any>();
  passEvent$ = this.passEventSubject.asObservable();
  public paymentDetails: any[] = [];
  private emitSaveOrder = new Subject<boolean>();
  emitSaveOrder$ = this.emitSaveOrder.asObservable();


  setPreviousUrl(url: string) {
    this.previousUrl = url;
  }
  getPreviousUrl(): string | null {
    return this.previousUrl;
  }
  clearPreviousUrl() {
    this.previousUrl = null; 
  }
  setRedirectToMembership(value: boolean) {
    this.redirectToMembership = value;
  }
  cartData$ = this.cartDataSource.asObservable();
  sidenavToggle$ = this.sidenavToggleSource.asObservable();
  drawerState$ = this.drawerState.asObservable();

  eventDates: string[] = [];
  isEventDate: any[] = [];
  public eventDetails = new BehaviorSubject<any[]>([]);
  
  constructor(
    private apiService: ApiService,
    private notification: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  updateCartData(cartData: any) {
    this.cartDataSource.next(cartData);
  }

  toggleSidenav(open: boolean) {
    this.sidenavToggleSource.next(open);
  }

  toggleDrawer(state: boolean) {
    this.drawerState.next(state);
  }

  async getDeliveryData(): Promise<any> {
    try {
      const data = await this.apiService.getDeliveryDates({}).toPromise();
      const response = data['response'];

      if (response.status === 1) {
        const upcomingEvents = response.data.upcoming_events;
        this.eventDetails.next(upcomingEvents);
        this.eventDates = upcomingEvents.map((event: any) => event.event_date);
        this.isEventDate = upcomingEvents.map(detail => ({
          event_date: detail.event_date,
          create_order: detail.create_order,
          is_skipped: detail.is_skipped,
        })); 

        sessionStorage.setItem('event_details', JSON.stringify(upcomingEvents));
        sessionStorage.setItem(
          'upcoming_event_dates',
          JSON.stringify(this.eventDates)
        );
        sessionStorage.setItem(
          'recurring_frequencies',
          JSON.stringify(response.data.frequency)
        );
        sessionStorage.setItem(
          'selected_delivery_date',this.eventDates[0]
        );

        return upcomingEvents;
      } else {
        this.handleErrorResponse(response);
        return false;
      }
    } catch (error) {
      console.log('Error in getDeliveryData:', error);
      return false;
    }
  }

  async getOrderData(payload: any = {}): Promise<any> {
    try {
      if (!isPlatformBrowser(this.platformId)) {
        return { orderSummary: [], cartCount: { items: 0, total: 0 } };
      }
  
      // Retrieve event details from session storage
      const eventDetails = sessionStorage.getItem('event_details');
      const eventDetailsParsed = eventDetails ? JSON.parse(eventDetails) : null;
  
      // Check if eventDetails is null or empty
      if (!eventDetailsParsed || eventDetailsParsed.length === 0) {
        // Call getDeliveryData to fetch event details
        const deliveryData = await this.getDeliveryData();
        
        // If deliveryData is invalid, return early
        if (!deliveryData || deliveryData.length === 0) {
          return { orderSummary: [], cartCount: { items: 0, total: 0 } };
        }
      }

      const result = await this.apiService.saleOrders(payload).toPromise();
      const response = result['response'];

      if (response.status === 1) {
        const orderSummary = this.processOrderSummary(response.data.sale_orders);
        const cartCount = this.calculateCartCount(orderSummary);

        this.cartDataSource.next(cartCount);
        return { orderSummary, cartCount, status: response.status };
      } else {
        this.cartDataSource.next({ items: 0, total: 0 });
        return { orderSummary: [], cartCount: { items: 0, total: 0 }, status: response.status };
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      return { orderSummary: [], cartCount: { items: 0, total: 0 } };
    }
  }

  private processOrderSummary(saleOrders: any[]): any[] {
    return saleOrders.map((order) => ({
      ...order,
      formattedDate: this.formatDate(order.date_of_delivered),
      weekLabel: this.getWeekLabel(order.date_of_delivered, this.isEventDate),
    }));
  }

  private calculateCartCount(orderSummary: any[]): { items: number; total: number } {
    return orderSummary.reduce(
      (acc, saleOrder: any) => {
        acc.items += saleOrder.sale_order_items.length;
        acc.total += saleOrder.total_payable;
        return acc;
      },
      { items: 0, total: 0 }
    );
  }

  private handleErrorResponse(response: any) {
    if (response.status === 2 || response.status === 3) {
      this.notification.error(response.message, 3000);
    }
  }

  getWeekLabel(deliveryDate: string, upcomingDates: { event_date: string; create_order: any; is_skipped: boolean;}[]): { week: string; create_order: any; is_skipped: boolean } {
    const delivery = new Date(deliveryDate).toISOString().split('T')[0]; // Format date to 'YYYY-MM-DD'
  
    let week: string;
    let create_order: any;
    let is_skipped: boolean;
  
    if (delivery === upcomingDates[0]?.event_date) {
      week = 'This Week';
      create_order = upcomingDates[0]?.create_order;
      is_skipped = upcomingDates[0]?.is_skipped;
    } else if (delivery === upcomingDates[1]?.event_date) {
      week = 'Next Week';
      create_order = upcomingDates[1]?.create_order;
      is_skipped = upcomingDates[1]?.is_skipped;
    } else {
      week = 'Other Week';
      create_order = upcomingDates[2]?.create_order
      is_skipped = upcomingDates[2]?.is_skipped
    }
  
    return { week, create_order, is_skipped };
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString.replace(/\\\//g, '/'));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  emitPassEvent(data: any) {
    this.passEventSubject.next(data);
  }
  emitSaveOrderFn() {
    this.emitSaveOrder.next(true);
  }
}
